import {
  createAsyncThunk,
  // createAsyncThunk,
  createSlice,
  PayloadAction
  // PayloadAction
} from '@reduxjs/toolkit'
import { medicareApiClient } from 'clients/medicareApiClient'
import { ILoadingStatus } from 'models/LoadingStatus'

// The body of every case reducer is implicitly wrapped with a call to
// `produce()` from the [immer](https://github.com/mweststrate/immer) library.
// This means that rather than returning a new state object, you can also
// mutate the passed-in state object directly; these mutations will then be
// automatically and efficiently translated into copies, giving you both
// convenience and immutability.
// CreateSlice uses CreateReducer which used immer produce

export interface IAuthRepsReducer extends IAuthRepsAction {
  status?: ILoadingStatus
}

interface IAuthRepsGetter {
  authReps: IAuthRepsReducer
}

export type INyDrugInformation = 'include_information' | 'exclude_information'
export type ILimitedInformation = 'any_information' | 'limited_information'

/**
 * Does a deep clone of the state so that we don't accidentally break data. Immer does not provide a deep clone
 * and many consider lodash's deep clone to be a code smell because it is easy to clone gigantic objects. With
 * this we limit our cloning to the reducer state itself.
 * @param state
 */
export const cloneAuthRepReducerState = (state: IAuthRepsReducer): IAuthRepsReducer => {
  return {
    id: state.id,
    repFullName: state.repFullName,
    organizationName: state.organizationName,
    repAddress: { ...state.repAddress },
    repRelationshipToBene: state.repRelationshipToBene,
    authStartDate: state.authStartDate ? { ...state.authStartDate } : undefined,
    authEndDate: state.authEndDate ? { ...state.authEndDate } : undefined,
    disclosureLimitations: { ...state.disclosureLimitations },
    disclosureReason: state.disclosureReason,
    acknowledgeCheckbox: state.acknowledgeCheckbox,
    disclosurePeriod: state.disclosurePeriod,
    nyDrugInformation: state.nyDrugInformation
  }
}

export interface IAuthRepsAction {
  id?: string,
  repFullName?: string,
  organizationName?: string,
  repAddress: eAuthorizedRepAddress,
  repRelationshipToBene?: string,
  authStartDate?: { // yyyy-mm-dd in backend
    day: string | number,
    month: string | number,
    year: string | number
  },
  authEndDate?: { // yyyy-mm-dd in backend
    day?: string | number,
    month?: string | number,
    year?: string | number
  },
  disclosureLimitations: eAuthorizedRepLimitations,
  disclosureReason?: string
  acknowledgeCheckbox?: boolean;
  disclosurePeriod?: 'disclose_specific_time' | 'disclose_indefinitely';
  nyDrugInformation?: INyDrugInformation;
  username?: string;
}

export interface eAuthorizedRepLimitations {
  limitedInformation: 'limited_information' | 'any_information'
  isInfoMedicareAllowedChecked?: boolean
  isInfoMedicareClaimsChecked?: boolean
  isInfoPlanEnrollmentChecked?: boolean
  isInfoPremiumPaymentsChecked?: boolean
  isOtherSpecificInfoChecked?: boolean
  otherSpecificInformation?: string
}

export interface eAuthorizedRepAddress {
  raw?: string
  address1?: string
  city?: string
  state?: string
  zip?: string
}

// will grab all reps, but only one will be added to state for editing purposes
// export const fetchRep = createAsyncThunk(
//   '/authorization/get',
//   async (payload: IAuthRepsReducer, { rejectWithValue, dispatch }) => {
//     // Pending addCase is executed, blocking I/O synchronously
//     try {
//       const result = await medicareApiClient.asyncGetRepsByReqToken()
//       // TODO: take reps get the rep we want
//       // THEN: update state
//       // dispatch(setRep(result))
//     } catch (error) {
//       if (!error.response) {
//         throw error // rejected addCase is executed
//       }
//       rejectWithValue(error.response.data)
//       throw error
//     }
//     return payload // fulfilled
//   })

export const submitAddRep = createAsyncThunk(
  '/authorization/post',
  async (payload: IAuthRepsReducer, { rejectWithValue, dispatch }) => {
    // Pending addCase is executed, blocking I/O synchronously
    try {
      await medicareApiClient.asyncAddRep(payload)
    } catch (error: any) {
      if (!error.response) {
        throw error // rejected addCase is executed
      }
      rejectWithValue(error.response.data)
      throw error
    }
    return payload // fulfilled
  })

export const submitEditRep = createAsyncThunk(
  '/authorization/put',
  async (payload: IAuthRepsReducer, { rejectWithValue, dispatch }) => {
    // Pending addCase is executed, blocking I/O synchronously
    try {
      await medicareApiClient.asyncUpdateRep(payload)
    } catch (error: any) {
      if (!error.response) {
        throw error // rejected addCase is executed
      }
      rejectWithValue(error.response.data)
      throw error
    }
    return payload // fulfilled
  })

export const authRepsSlice = createSlice({
  name: 'authReps',
  initialState: {
    status: undefined, // Form submmission
    repFullName: undefined,
    organizationName: undefined,
    repAddress: {} as eAuthorizedRepAddress,
    repRelationshipToBene: '',
    authStartDate: undefined,
    authEndDate: undefined,
    disclosurePeriod: undefined,
    disclosureReason: undefined,
    disclosureLimitations: {} as eAuthorizedRepLimitations,
    acknowledgeCheckbox: undefined
  } as IAuthRepsReducer,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(submitAddRep.pending, (state: IAuthRepsReducer, { meta }) => {
        state.status = 'loading'
      })
      .addCase(submitAddRep.fulfilled, (state: IAuthRepsReducer, action: PayloadAction<IAuthRepsAction>) => {
        state.status = 'finished'
      })
      .addCase(submitAddRep.rejected, (state: IAuthRepsReducer) => {
        state.status = 'error'
      })
      .addCase(submitEditRep.pending, (state: IAuthRepsReducer, { meta }) => {
        state.status = 'loading'
      })
      .addCase(submitEditRep.fulfilled, (state: IAuthRepsReducer, action: PayloadAction<IAuthRepsAction>) => {
        state.status = 'finished'
      })
      .addCase(submitEditRep.rejected, (state: IAuthRepsReducer) => {
        state.status = 'error'
      })
  }
})

// GETTERS
export const getAuthReps = (state: IAuthRepsGetter) => {
  return state.authReps
}

export const getLoadingState = (state: IAuthRepsGetter) => {
  return state.authReps.status
}

export default authRepsSlice.reducer
